import type { TapeUploadStep } from '../TapeUploadStepper';

// Tapestry pipeline stage model (ENG-2790).
//
// `current_stage` is a plain String on the GraphQL schema (no enum), so the
// client owns the canonical set of values it branches on. These mirror the
// api resolver's source of truth (`tapestryJobStatus.ts` → PIPELINE_STAGE_ORDER
// and the lifecycle/handleFailure terminal stages on the soumya/tapestry
// branch). Keep this in sync if the backend stage vocabulary changes.
//
// "Tapestry pipeline" is always qualified — bare "Pipeline" is AWS CodePipeline
// elsewhere in the glossary.

export type TapestryStage =
  | 'INITIALIZING'
  | 'DECOMPOSING'
  | 'CLASSIFYING'
  | 'DETECTING_REGIONS'
  | 'EXTRACTING'
  | 'MAPPING'
  | 'WAITING_FOR_MAPPING'
  | 'VALIDATING'
  // Transient post-VALIDATING stage emitted by the api validate step before the
  // pipeline advances to the review gate / persist (api tapestryJobStatus.ts
  // VALIDATION_OUTCOME_STAGES). The run is still in progress here.
  | 'VALIDATED'
  | 'WAITING_FOR_REVIEW'
  | 'PERSISTING'
  // Transient stage written by Persist before async Finalize promotes the run
  // to COMPLETED (api comment: "Persist writes tapestry_stage=PERSISTED before
  // async Finalize promotes to COMPLETED"). Still in progress here.
  | 'PERSISTED'
  | 'COMPLETED'
  | 'FAILED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'NO_RECORDS_EXTRACTED'
  // Written by the api humanGateTimeout step when a human gate expires (File.state = ERROR).
  | 'MAPPING_TIMEOUT'
  | 'REVIEW_TIMEOUT'
  | 'UNKNOWN';

// Per-stage status as emitted by TapestryStageStatus.status.
export type TapestryStageStatusValue = 'pending' | 'in_progress' | 'completed';

// Ordered Tapestry pipeline stages, matching the api PIPELINE_STAGE_ORDER.
// `WAITING_FOR_MAPPING` / `WAITING_FOR_REVIEW` are the two human gates that sit
// inline in this order; the PipelineStatus UI (ENG-2791) renders this sequence.
export const TAPESTRY_PIPELINE_STAGES: readonly TapestryStage[] = [
  'DECOMPOSING',
  'CLASSIFYING',
  'DETECTING_REGIONS',
  'EXTRACTING',
  'WAITING_FOR_MAPPING',
  'VALIDATING',
  'WAITING_FOR_REVIEW',
  'PERSISTING',
  'COMPLETED',
] as const;

// Stages where the pipeline is paused for a user action (human gates).
export const WAITING_STAGES: ReadonlySet<TapestryStage> = new Set<TapestryStage>([
  'WAITING_FOR_MAPPING',
  'WAITING_FOR_REVIEW',
]);

// Stages where the pipeline has stopped and will not advance on its own.
// COMPLETED is a success terminal; the rest are failure/abort terminals.
export const TERMINAL_STAGES: ReadonlySet<TapestryStage> = new Set<TapestryStage>([
  'COMPLETED',
  'FAILED',
  'REJECTED',
  'CANCELLED',
  'NO_RECORDS_EXTRACTED',
  'MAPPING_TIMEOUT',
  'REVIEW_TIMEOUT',
]);

// Failure/abort terminals (drives the error/recovery panel in ENG-2794).
// NO_RECORDS_EXTRACTED is emitted by the api validate step when extraction
// produced zero records (it also sets File.state = ERROR).
export const ERROR_STAGES: ReadonlySet<TapestryStage> = new Set<TapestryStage>([
  'FAILED',
  'REJECTED',
  'CANCELLED',
  'NO_RECORDS_EXTRACTED',
  'MAPPING_TIMEOUT',
  'REVIEW_TIMEOUT',
]);

// Stages where the pipeline is doing automated work and the UI should keep
// polling. Defined as an explicit allow-list (not "everything that isn't
// waiting/terminal") so UNKNOWN and any unrecognized stage string do NOT cause
// an indefinite poll loop. MAPPING is the transient stage while correction
// submission is in flight, so it counts as active.
export const ACTIVE_STAGES: ReadonlySet<TapestryStage> = new Set<TapestryStage>([
  'INITIALIZING',
  'DECOMPOSING',
  'CLASSIFYING',
  'DETECTING_REGIONS',
  'EXTRACTING',
  'MAPPING',
  'VALIDATING',
  // Transient in-progress stages — the run is still advancing on its own, so
  // the UI must keep polling through them (see useTapestryJob).
  'VALIDATED',
  'PERSISTING',
  'PERSISTED',
]);

export const isWaitingStage = (stage: TapestryStage | string | undefined): boolean =>
  WAITING_STAGES.has(stage as TapestryStage);

export const isTerminalStage = (stage: TapestryStage | string | undefined): boolean =>
  TERMINAL_STAGES.has(stage as TapestryStage);

export const isErrorStage = (stage: TapestryStage | string | undefined): boolean =>
  ERROR_STAGES.has(stage as TapestryStage);

// "Active" = the pipeline is doing automated work and the UI should keep
// polling for progress. Waiting (paused for the user), terminal, and
// unrecognized stages (e.g. UNKNOWN) are not active.
export const isActiveStage = (stage: TapestryStage | string | undefined): boolean =>
  ACTIVE_STAGES.has(stage as TapestryStage);

// View of the Tapestry flags the api exposes on the File type (ENG-2788).
// `GetLoanTape` selects `tapestry_enabled`/`tapestry_stage`, so callers pass the
// file object structurally and `isTapestryFile` reads the flag; for legacy
// uploads it is false and the Tapestry branch stays inert.
export interface TapestryFileFlags {
  tapestry_enabled?: boolean | null;
  tapestry_stage?: string | null;
}

export const isTapestryFile = (file?: TapestryFileFlags | null): boolean =>
  Boolean(file?.tapestry_enabled);

// ---------------------------------------------------------------------------
// Completion outcome — the primary terminal router for the Tapestry UI.
//
// `TapestryJobStatus.completion_outcome` is a plain String on the schema. These
// mirror the api `TAPESTRY_COMPLETION_OUTCOMES` (tapestryJobStatus.ts). The UI
// routes terminal/error rendering off THIS value, not `current_stage` — e.g.
// an all-duplicates run ends `success_no_persist` (info, not error) and a zero-
// extraction run ends `no_records`, neither of which is a FAILED stage.
// ---------------------------------------------------------------------------
export type TapestryCompletionOutcome =
  | 'in_progress'
  | 'success'
  | 'success_no_persist'
  | 'no_records'
  | 'validation_error'
  | 'pipeline_failed'
  | 'cancelled'
  | 'rejected';

// Outcomes that mean the run is still moving; the UI shows live pipeline status.
export const isInProgressOutcome = (
  outcome: TapestryCompletionOutcome | string | undefined,
): boolean => outcome === 'in_progress';

// Any outcome other than in_progress is terminal (success or otherwise).
export const isTerminalOutcome = (
  outcome: TapestryCompletionOutcome | string | undefined,
): boolean => outcome !== undefined && outcome !== 'in_progress';

// Outcomes the user can act on for recovery (retry/re-upload) — drives the
// error panel in ENG-2794. `cancelled`/`rejected` are user-initiated aborts and
// `success_no_persist`/`no_records` are non-error terminals, so none are errors.
const ERROR_OUTCOMES: ReadonlySet<string> = new Set<TapestryCompletionOutcome>([
  'validation_error',
  'pipeline_failed',
]);

export const isErrorOutcome = (outcome: TapestryCompletionOutcome | string | undefined): boolean =>
  ERROR_OUTCOMES.has(outcome as TapestryCompletionOutcome);

// ---------------------------------------------------------------------------
// Pipeline step display config.
//
// The PipelineStatus stepper (ENG-2791) renders the seven `TapestryStageStatus`
// nodes on `TapestryJobStatus` in order. `key` matches the GraphQL field name;
// `label` is the short user-facing step name. This is the node sequence, which
// is distinct from `current_stage` strings (e.g. the `review` node corresponds
// to the WAITING_FOR_REVIEW stage).
// ---------------------------------------------------------------------------
export type TapestryStageNodeKey =
  | 'decomposition'
  | 'classification'
  | 'detection'
  | 'extraction'
  | 'validation'
  | 'persistence'
  | 'review';

export interface TapestryStageNodeConfig {
  key: TapestryStageNodeKey;
  label: string;
}

export const TAPESTRY_STAGE_NODES: readonly TapestryStageNodeConfig[] = [
  { key: 'decomposition', label: 'Decompose' },
  { key: 'classification', label: 'Classify' },
  { key: 'detection', label: 'Detect' },
  { key: 'extraction', label: 'Extract' },
  { key: 'validation', label: 'Validate' },
  { key: 'review', label: 'Review' },
  { key: 'persistence', label: 'Persist' },
] as const;

// ---------------------------------------------------------------------------
// Upload-history status label.
//
// Maps a file's `tapestry_stage` (the same vocabulary as current_stage) to a
// compact status pill for the TapesUploadedTable, parallel to the legacy
// TAPE_STATE_STRINGS. Unknown / in-flight stages collapse to "Processing".
// ---------------------------------------------------------------------------
export interface TapestryStatusDisplay {
  label: string;
  colorClass: string;
  textColor: string;
}

export function tapestryStageDisplay(stage: string | null | undefined): TapestryStatusDisplay {
  switch (stage) {
    case 'WAITING_FOR_MAPPING':
      return { label: 'Needs mapping', colorClass: 'bg-blue-400', textColor: 'text-white' };
    case 'WAITING_FOR_REVIEW':
      return { label: 'Needs review', colorClass: 'bg-blue-400', textColor: 'text-white' };
    case 'COMPLETED':
      // Neutral "Complete" rather than "Saved": the table only sees
      // tapestry_stage, and an all-duplicates run also finishes COMPLETED
      // (completion_outcome=success_no_persist) with nothing persisted, so
      // "Saved" would overclaim. The outcome panel shows the precise result.
      return { label: 'Complete', colorClass: 'bg-green-500', textColor: 'text-white' };
    case 'FAILED':
      return { label: 'Failed', colorClass: 'bg-red-500', textColor: 'text-white' };
    case 'NO_RECORDS_EXTRACTED':
      return { label: 'No records', colorClass: 'bg-red-400', textColor: 'text-white' };
    case 'REJECTED':
      return { label: 'Rejected', colorClass: 'bg-slate-400', textColor: 'text-white' };
    case 'CANCELLED':
      return { label: 'Cancelled', colorClass: 'bg-slate-400', textColor: 'text-white' };
    case 'MAPPING_TIMEOUT':
      return { label: 'Mapping timed out', colorClass: 'bg-red-500', textColor: 'text-white' };
    case 'REVIEW_TIMEOUT':
      return { label: 'Review timed out', colorClass: 'bg-red-500', textColor: 'text-white' };
    default:
      // INITIALIZING / DECOMPOSING / … / VALIDATING / PERSISTING / null: still working.
      return { label: 'Processing', colorClass: 'bg-yellow-400', textColor: 'text-gray-950' };
  }
}

// Stage for running copy. Terminal stages take the status-pill label so a stopped run never
// reads as a pipeline step; the rest are sentence-cased ('WAITING_FOR_MAPPING' → 'Waiting
// for mapping').
export const humanizeTapestryStage = (stage: string): string => {
  if (isTerminalStage(stage)) {
    return tapestryStageDisplay(stage).label;
  }
  const words = stage.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// ---------------------------------------------------------------------------
// Stepper position.
//
// Map a Tapestry stage onto the 3-step Upload/Map/Review stepper. Pre-gate
// automated stages are step 1; the mapping gate and its transient post-submit
// MAPPING stage are step 2; validation onward (review gate, persist, completion)
// is step 3. MAPPING and VALIDATED are transient stages enumerated explicitly so
// they don't fall through to the default and snap the stepper back to step 1.
// ---------------------------------------------------------------------------
export const tapestryStageToStep = (stage: string | null | undefined): TapeUploadStep => {
  switch (stage) {
    case 'MAPPING':
    case 'WAITING_FOR_MAPPING':
      return 2;
    case 'VALIDATING':
    case 'VALIDATED':
    case 'WAITING_FOR_REVIEW':
    case 'PERSISTING':
    case 'PERSISTED':
    case 'COMPLETED':
      return 3;
    default:
      return 1;
  }
};
